/**
 * Провайдеры Refine — копия supply.
 *
 * Сессия живёт в httpOnly-cookie, которую JS не видит вовсе. Поэтому `check`
 * спрашивает сервер (`/api/me`), а не хранилище: узнать, жива ли сессия, можно
 * только у того, кто её выдал.
 */

import type { AuthProvider, DataProvider } from "@refinedev/core";

import { apiFetch, ApiFailure } from "@/lib/api";

/** Первый экран после входа. */
export const HOME = "/statements";

/** Кто вошёл. Приходит из CRM (`sUser`), своей таблицы пользователей нет. */
export type CurrentUser = {
  id: number;
  name: string;
  short: string;
  /** База CRM, по которой сверяется пароль: `crm-prod` или `crm-dev`. */
  crm?: string;
  crm_is_prod?: boolean;
  /** Коды полномочий finance. Решает сервер; список нужен, чтобы не показывать то, что откажут. */
  permissions: string[];
};

export const authProvider: AuthProvider = {
  check: async () => {
    try {
      await apiFetch<CurrentUser>("/api/me");
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: "/login", logout: true };
    }
  },

  // Одно поле — пароль: он же идентифицирует человека в CRM.
  login: async ({ password }: { password: string }) => {
    try {
      await apiFetch<CurrentUser>("/api/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      return { success: true, redirectTo: HOME };
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;
      return {
        success: false,
        error: {
          name: failure?.code ?? "LOGIN_FAILED",
          message: failure?.message ?? "Не удалось войти",
        },
      };
    }
  },

  logout: async () => {
    // Сессию гасит сервер: иначе украденная cookie продолжала бы работать.
    try {
      await apiFetch<{ ok: boolean }>("/api/logout", { method: "POST" });
    } catch {
      // Даже если сервер не ответил, уводим на форму входа.
    }
    return { success: true, redirectTo: "/login" };
  },

  getIdentity: async () => {
    // «Никто не вошёл» — только ответ 401. Сорвавшийся запрос — ошибка, а не
    // пустой пользователь: экран не гаснет из-за одной осечки сети.
    try {
      return await apiFetch<CurrentUser>("/api/me");
    } catch (error) {
      if (error instanceof ApiFailure && error.status === 401) return null;
      throw error;
    }
  },

  onError: async (error) => {
    if (error instanceof ApiFailure && error.status === 401) {
      return { logout: true, redirectTo: "/login", error };
    }
    return { error };
  },
};

/**
 * dataProvider Refine обязателен, но экраны ходят в API сами (`@/lib/*`):
 * ресурсов-таблиц «на всё» здесь нет. Заглушки на весь интерфейс — код,
 * который никто не проверял, но все считают рабочим.
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => "/api",
  getList: async () => {
    throw new Error("getList не используется: экраны зовут API напрямую");
  },
  getOne: async () => {
    throw new Error("getOne не используется");
  },
  create: async () => {
    throw new Error("create не используется");
  },
  update: async () => {
    throw new Error("update не используется");
  },
  deleteOne: async () => {
    throw new Error("deleteOne не используется");
  },
};
